<template>
  <div :class="classList" class="banners-selector">
    <searchable-articles-list
      v-if="showSearchList"
      ref="searchableArticleList"
      :query="query"
      :value="selectableArticles"
      :disabled="disabled"
      :has-more-articles="hasMoreArticles"
      :hide-search="hideSearch"
      class="search-list"
      @queryChange="handleQueryChange"
      @select="handleSelectArticle"
      @getMoreArticles="setupArticles"
    ></searchable-articles-list>
    <selected-articles-list
      :disabled="disabled"
      :value="value"
      class="selected-list"
      @sortedValue="handleValueSorted"
      @remove="handleRemoveArticle"
      @removeAll="handleRemoveAllArticle"
    ></selected-articles-list>
  </div>
</template>

<script>
import { debounce } from "@/lib/util"
import differenceWith from "ramda/src/differenceWith"
import { articleUseCase } from "../../../dependencies"
import SearchableArticlesList from "./SearchableArticlesList"
import SelectedArticlesList from "./SelectedArticlesList"

export default {
  components: {
    SelectedArticlesList,
    SearchableArticlesList,
  },
  props: {
    initialArticles: {
      type: Array,
      default: null,
    },
    value: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      query: "",
      hideSearch: true,
      allArticles: [],
      isLoading: false,
      page: 1,
      hasMoreArticles: false,
      totalArticles: 1,
    }
  },
  computed: {
    selectableArticles() {
      return differenceWith(this.compareArticle, this.allArticles, this.value)
    },
    showSearchList() {
      return !this.disabled
    },
    classList() {
      return { disabled: this.disabled }
    },
    queryParams() {
      return {
        searchText: this.query,
        page: this.page,
      }
    },
  },
  watch: {
    query() {
      this.watchQuery()
    },
  },
  created() {
    this.setup()
  },
  methods: {
    setup() {
      this.setupArticles()
    },
    async setupArticles() {
      if (this.allArticles.length === this.totalArticles) {
        this.hasMoreArticles = false
        return
      }
      const articles = await this.fetchArticles()
      this.page += 1
      this.hasMoreArticles = true

      const newArticles = articles.map((article) => {
        const { id, title, content, category, imageUrl } = article

        return {
          id,
          title,
          content,
          category,
          imageUrl,
        }
      })

      this.allArticles = this.allArticles.concat(newArticles)
    },
    async fetchArticles() {
      if (this.initialArticles) {
        return this.initialArticles
      }

      this.isLoading = true
      const { articleBanners, count } = await articleUseCase.listBanner(
        this.queryParams,
      )

      this.isLoading = false
      this.totalArticles = count
      return articleBanners
    },
    handleSelectArticle(article) {
      this.updateInput([...this.value, article])
    },
    handleRemoveArticle(article) {
      this.updateInput(this.value.filter(({ id }) => article.id !== id))
    },
    async handleRemoveAllArticle() {
      try {
        await this.$confirm("คุณต้องการลบรายการใช่หรือไม่")
      } catch {
        return
      }

      this.updateInput([])
    },
    handleValueSorted(value) {
      this.updateInput(value)
    },
    updateInput(value) {
      this.$emit("input", value)
    },
    compareArticle(article, article2) {
      return article.id === article2.id
    },
    handleQueryChange(value) {
      this.query = value
    },
    watchQuery: debounce(function watchQuery() {
      this.resetData()
      this.setupArticles()
      this.$refs.searchableArticleList.resetLoading()
    }, 1.5),
    resetData() {
      this.allArticles = []
      this.totalArticles = 1
      this.page = 1
      this.hasMoreArticles = false
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.banners-selector {
  display: grid;
  grid-template-columns: 1fr 1fr;

  > .search-list {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  > .selected-list {
    border-left-width: 0;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
}

.banners-selector.disabled {
  display: block;

  > .selected-list {
    border-left-width: 1px;
    border-radius: $border-radius;
  }
}
</style>
